import React, { useState, useEffect, useCallback } from 'react';
import { SessionState, MaterializedView, NpcDefinition } from '@/types';
import { api } from '@/services/api';
import { Mail, ExpandMore, ExpandLess, Visibility } from '@mui/icons-material';
import { MessageSquare } from 'lucide-react';

interface ContactsCentricPanelProps {
    session: SessionState;
    materializedView: MaterializedView;
    npcs: NpcDefinition[];
    avatarUrls?: Record<string, string>;
    onSendEmail?: (to: string) => void;
    onOpenSlack?: (channelId: string) => void;
}

const POWER_LEVEL_LABELS: Record<string, string> = {
    'direct-report': 'Direct Report',
    'peer': 'Peer',
    'senior': 'Senior',
    'external': 'External',
};

const POWER_LEVEL_COLORS: Record<string, string> = {
    'direct-report': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    'peer': 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    'senior': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    'external': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
};

const TRUST_RING: Record<string, string> = {
    positive: 'ring-green-500/60',
    neutral: 'ring-slate-500/40',
    waiting: 'ring-amber-500/60',
    frustrated: 'ring-red-500/60',
};

const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const getTrustState = (rel?: { trust: number; frustration: number; escalationLevel: number }): 'positive' | 'neutral' | 'waiting' | 'frustrated' => {
    if (!rel) return 'neutral';
    if (rel.escalationLevel >= 2 || rel.frustration >= 0.5) return 'frustrated';
    if (rel.escalationLevel >= 1 || rel.frustration >= 0.3) return 'waiting';
    if (rel.trust >= 0.6) return 'positive';
    return 'neutral';
};

const ContactsCentricPanel: React.FC<ContactsCentricPanelProps> = ({
    session,
    materializedView,
    npcs,
    avatarUrls = {},
    onSendEmail,
    onOpenSlack,
}) => {
    const [openedProfiles, setOpenedProfiles] = useState<Set<string>>(
        () => new Set(materializedView.behavior?.profilesOpened ?? [])
    );
    const [expandedContactId, setExpandedContactId] = useState<string | null>(null);

    useEffect(() => {
        const fromMv = materializedView.behavior?.profilesOpened ?? [];
        setOpenedProfiles(prev => {
            const next = new Set(prev);
            fromMv.forEach(id => next.add(id));
            return next;
        });
    }, [materializedView.behavior?.profilesOpened]);

    const handleToggleProfile = useCallback(async (npc: NpcDefinition) => {
        const isExpanding = expandedContactId !== npc.id;
        setExpandedContactId(isExpanding ? npc.id : null);

        if (isExpanding && !openedProfiles.has(npc.id) && npc.contactProfile) {
            setOpenedProfiles(prev => new Set([...prev, npc.id]));
            try {
                await api.viewContactProfile(session.id, npc.id);
            } catch {
                // Non-fatal — tracking failure doesn't affect gameplay.
            }
        }
    }, [expandedContactId, openedProfiles, session.id]);

    const relationships = materializedView.relationships ?? {};
    const expandedNpc = npcs.find(n => n.id === expandedContactId) ?? null;

    return (
        <div className="flex flex-col h-full bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between shrink-0">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">People</h3>
                <span className="text-[10px] text-slate-500">{npcs.length} contacts</span>
            </div>

            <div className="flex flex-1 min-h-0">
                {/* 2×4 contact grid */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="grid grid-cols-4 gap-3">
                        {npcs.map((npc) => {
                            const profile = npc.contactProfile;
                            const isSelected = expandedContactId === npc.id;
                            const hasBeenOpened = openedProfiles.has(npc.id);
                            const rel = relationships[npc.id];
                            const trustState = getTrustState(rel);
                            const avatarUrl = avatarUrls[npc.id];
                            const powerLevelLabel = profile?.powerLevel
                                ? POWER_LEVEL_LABELS[profile.powerLevel] ?? profile.powerLevel
                                : null;
                            const powerLevelColor = profile?.powerLevel
                                ? POWER_LEVEL_COLORS[profile.powerLevel] ?? 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                                : null;

                            return (
                                <button
                                    key={npc.id}
                                    onClick={() => handleToggleProfile(npc)}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all text-center cursor-pointer
                                        ${isSelected
                                            ? 'bg-slate-700/80 border-blue-500/50'
                                            : 'bg-slate-900/60 border-slate-700 hover:border-slate-500 hover:bg-slate-800/60'
                                        }`}
                                >
                                    {/* Photo / Avatar */}
                                    <div className={`relative size-16 rounded-full ring-2 ${TRUST_RING[trustState]} shrink-0`}>
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt={npc.name}
                                                className="size-full rounded-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className="size-full rounded-full bg-slate-600 flex items-center justify-center text-lg font-bold text-white">
                                                {getInitials(npc.name)}
                                            </div>
                                        )}
                                        {/* Profile-opened indicator */}
                                        {hasBeenOpened && (
                                            <div className="absolute -bottom-0.5 -right-0.5 size-4 rounded-full bg-blue-500 border-2 border-slate-800 flex items-center justify-center">
                                                <Visibility style={{ fontSize: '9px' }} className="text-white" />
                                            </div>
                                        )}
                                        {/* Slack channel indicator */}
                                        {npc.preferredChannel === 'slack' && (
                                            <div className="absolute -top-0.5 -right-0.5 size-4 rounded-full bg-violet-600 border-2 border-slate-800 flex items-center justify-center">
                                                <MessageSquare size={8} className="text-white" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Name */}
                                    <div className="w-full">
                                        <p className="text-sm font-bold text-white leading-tight">{npc.name}</p>
                                        <p className="text-[11px] text-slate-400 truncate mt-0.5">{npc.role}</p>
                                    </div>

                                    {/* Power level chip */}
                                    {powerLevelLabel && (
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${powerLevelColor}`}>
                                            {powerLevelLabel}
                                        </span>
                                    )}

                                    {/* Expand caret */}
                                    {profile?.styleNotes && (
                                        <div className="text-slate-500">
                                            {isSelected
                                                ? <ExpandLess style={{ fontSize: '16px' }} />
                                                : <ExpandMore style={{ fontSize: '16px' }} />
                                            }
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Profile drawer — right panel, shown when a contact is selected */}
                {expandedNpc && (
                    <div className="w-64 shrink-0 border-l border-slate-700 flex flex-col overflow-y-auto">
                        <div className="p-4 flex flex-col gap-4">
                            {/* Large photo */}
                            <div className="flex flex-col items-center gap-3 pt-2">
                                <div className={`size-20 rounded-full ring-2 ${TRUST_RING[getTrustState(relationships[expandedNpc.id])]} overflow-hidden`}>
                                    {avatarUrls[expandedNpc.id] ? (
                                        <img
                                            src={avatarUrls[expandedNpc.id]}
                                            alt={expandedNpc.name}
                                            className="size-full object-cover"
                                        />
                                    ) : (
                                        <div className="size-full bg-slate-600 flex items-center justify-center text-xl font-bold text-white">
                                            {getInitials(expandedNpc.name)}
                                        </div>
                                    )}
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-bold text-white">{expandedNpc.name}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{expandedNpc.role}</p>
                                    {expandedNpc.contactProfile?.powerLevel && (
                                        <span className={`mt-1 inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border
                                            ${POWER_LEVEL_COLORS[expandedNpc.contactProfile.powerLevel] ?? 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                                            {POWER_LEVEL_LABELS[expandedNpc.contactProfile.powerLevel] ?? expandedNpc.contactProfile.powerLevel}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Context — always shown */}
                            {expandedNpc.contactProfile?.relationshipContext && (
                                <div>
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Context</p>
                                    <p className="text-[12px] text-slate-300 leading-relaxed">{expandedNpc.contactProfile.relationshipContext}</p>
                                </div>
                            )}

                            {/* Style notes — revealed on open */}
                            {expandedNpc.contactProfile?.styleNotes && openedProfiles.has(expandedNpc.id) && (
                                <div>
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Style notes</p>
                                    <p className="text-[12px] text-slate-300 leading-relaxed">{expandedNpc.contactProfile.styleNotes}</p>
                                </div>
                            )}

                            {/* Channel CTA — Slack for Slack-preferred contacts, email otherwise */}
                            {expandedNpc.preferredChannel === 'slack' && onOpenSlack ? (
                                <button
                                    onClick={() => onOpenSlack(expandedNpc.id)}
                                    className="mt-auto flex items-center justify-center gap-2 w-full py-2 px-3 rounded-lg bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-400 hover:text-violet-300 text-xs font-bold transition-colors"
                                >
                                    <MessageSquare size={14} />
                                    Message in Slack
                                </button>
                            ) : onSendEmail ? (
                                <button
                                    onClick={() => onSendEmail(expandedNpc.email || `${expandedNpc.id}@example.com`)}
                                    className="mt-auto flex items-center justify-center gap-2 w-full py-2 px-3 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 hover:text-blue-300 text-xs font-bold transition-colors"
                                >
                                    <Mail style={{ fontSize: '14px' }} />
                                    Send Email
                                </button>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactsCentricPanel;
